import React, { useState, useRef, useCallback } from 'react';
import './ImportModal.css';
// Le découpeur de collage du module Étudiants (guillemets, tabulations ou points-virgules) :
// un seul parseur pour tous les collages de l'application, pas un second « à peu près ».
import { matriceDepuisTexte } from '../services/apogeeImport';

/** Nom passé à `onPreview` / `onImport` (meta.fichier) quand le tableau vient d'un COLLAGE. */
export const NOM_COLLAGE = 'collage';

/** Nombre de lignes collées, en-tête comprise — comptées APRÈS découpage : une cellule d'en-tête sur deux lignes dans
 *  Excel (« Tuteurs⏎Pédagogique », entre guillemets) reste UNE ligne (vu sur le tableau d'une assistante, 2026-10-14). */
function lignesCollees(texte) {
    const d = parseCollage(texte);
    return d.headers.length ? d.rows.length + 1 : 0;
}

/**
 * Le texte collé (Excel, ENT, tableur en ligne…) → le MÊME objet qu'un fichier : { headers, rows }.
 * La 1ʳᵉ ligne non vide est celle des en-têtes ; chaque ligne est ramenée à la largeur des en-têtes.
 */
function parseCollage(texte) {
    const matrice = matriceDepuisTexte(texte).filter((l) => l.some((c) => String(c ?? '').trim() !== ''));
    if (!matrice.length) return { headers: [], rows: [] };
    const headers = matrice[0].map((h) => String(h ?? '').trim());
    const rows = matrice.slice(1).map((l) => headers.map((_, i) => String(l[i] ?? '').trim()));
    return { headers, rows };
}

// ─── Parsing CSV natif ────────────────────────────────────────
function parseCSV(text) {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length === 0) return { headers: [], rows: [] };
    const parseRow = (line) => {
        const cells = []; let current = ''; let inQuote = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { inQuote = !inQuote; }
            else if ((ch === ',' || ch === ';') && !inQuote) { cells.push(current.trim()); current = ''; }
            else { current += ch; }
        }
        cells.push(current.trim()); return cells;
    };
    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(parseRow);
    return { headers, rows };
}

// ─── Parsing XLSX/XLSM via SheetJS ────────────────────────────
function sheetToData(XLSX, workbook, sheetName) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return { headers: [], rows: [] };
    const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const headers = (raw[0] || []).map(String);
    const rows = raw.slice(1).map(r => headers.map((_, i) => String(r[i] ?? '')));
    return { headers, rows };
}

async function parseXLSX(file, sheetName = null) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            import('xlsx').then(XLSX => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const target = sheetName || workbook.SheetNames[0];
                const parsed = sheetToData(XLSX, workbook, target);
                resolve({ ...parsed, workbook, sheetNames: workbook.SheetNames, selectedSheet: target, XLSX });
            }).catch(reject);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
}

/** Une ligne de diff qu'on peut cocher : ni identique, ni rejetée. */
const estImportable = (it) => it.status !== 'identical' && it.status !== 'rejete';

// ─── Mémoire de la correspondance (lot 3.3 bis) ───────────────
// ⚠️ Retenue par NOM de colonne, jamais par position : un fichier dont les colonnes
// ont bougé retrouve quand même la sienne. Un nom normalisé (casse, espaces) pour
// qu'« E-mail » et « e-mail » soient la même colonne.
const nomColonne = (h) => String(h || '').trim().toLowerCase().replace(/\s+/g, ' ');

function lireMemoire(cle) {
    if (!cle) return null;
    try {
        const brut = localStorage.getItem(cle);
        const lu = brut ? JSON.parse(brut) : null;
        return lu && typeof lu === 'object' ? lu : null;
    } catch (_) { return null; }          // stockage bloqué : pas de mémoire, pas de panne
}

function ecrireMemoire(cle, headers, mapping) {
    if (!cle) return;
    try {
        const precedente = lireMemoire(cle) || {};
        headers.forEach((h, i) => { if (nomColonne(h)) precedente[nomColonne(h)] = mapping[i] || ''; });
        localStorage.setItem(cle, JSON.stringify(precedente));
    } catch (_) { /* sans effet */ }
}

/** Applique la mémoire sur une correspondance automatique ; rend le nombre de colonnes reprises. */
function appliquerMemoire(cle, headers, autoMap, champs) {
    const memoire = lireMemoire(cle);
    if (!memoire) return 0;
    const connus = new Set(champs.map((c) => c.key));
    let n = 0;
    headers.forEach((h, i) => {
        const v = memoire[nomColonne(h)];
        // Un champ qui n'existe plus dans l'application est ignoré, pas imposé.
        if (v !== undefined && (v === '' || connus.has(v))) { autoMap[i] = v; n++; }
    });
    return n;
}

// ─── Composant principal ──────────────────────────────────────
// Props :
//   titre         string    — ex: "Programme national"
//   champs        Array     — [{key, label, required}]
//   contextFields Array     — [{key, label, required, options}] sélecteurs injectés dans chaque ligne
//   onPreview     async fn? — (rows) => [{status:'new'|'modified'|'identical', row, existing, changes:[{key,label,old,new}]}]
//   onImport      async fn  — (rows) => {success, errors, skipped}
//   onClose       fn
//
// Options ajoutées au lot 3.3 bis (Stage) — facultatives, les imports existants ne
// les passent pas et ne voient aucune différence :
//   memoireCle    string?   — si fournie, la correspondance des colonnes est MÉMORISÉE
//                             sous cette clé (localStorage) et reprise au fichier suivant.
//                             ⚠️ La clé doit porter l'identifiant du COMPTE (convention E29).
//   onPreview / onImport reçoivent un 2ᵉ argument { fichier } : le nom du fichier.
//   Un élément de diff peut porter :
//     status 'rejete' + raison  — montré, jamais cochable (ex. numéro inconnu)
//     libelle                   — ce qui identifie la ligne à l'écran
//     decoche + avertissement   — importable, mais NON coché par défaut
//     note                      — une précision neutre (ex. « rattaché par le nom »)
//   validerCorrespondance fn? — (clésAssociées[]) => message | null : une règle que
//                             `required` ne sait pas dire (ex. « le numéro OU le nom »)
//   nonImportables [{ libelle, raisonCourte, message, motif }]? — (2026-10-14) ce qui NE s'importe PAS ici, et pourquoi :
//                             montré grisé (non sélectionnable) en bas de chaque liste, et, sous une colonne dont
//                             l'en-tête répond à `motif` (RegExp), un message — l'assistante comprend que c'est voulu.
function ImportModal({ titre, champs, contextFields = [], onPreview, onImport, onClose, memoireCle = null,
                      validerCorrespondance = null, nonImportables = [] }) {
    const hasDiff = !!onPreview;
    const [memoireReprise, setMemoireReprise] = useState(0);

    // etape: 1=fichier 2=mapping 3=diff(si onPreview) 'R'=résultat
    const [etape, setEtape]             = useState(1);
    const [contextValues, setContextValues] = useState(() => {
        const init = {}; contextFields.forEach(f => { init[f.key] = ''; }); return init;
    });
    const [fichier, setFichier]         = useState(null);
    const [parsed, setParsed]           = useState(null);
    const [mapping, setMapping]         = useState({});
    const [dragOver, setDragOver]       = useState(false);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState(null);
    const [resultat, setResultat]       = useState(null);
    // Feuilles Excel
    const [sheetNames, setSheetNames]   = useState([]);
    const [selectedSheet, setSelectedSheet] = useState('');
    const wbRef  = useRef(null); // workbook SheetJS
    const xlsRef = useRef(null); // module XLSX
    // diff state
    const [diffItems, setDiffItems]     = useState([]);
    const [diffSel, setDiffSel]         = useState(new Set());
    const inputRef = useRef();
    // Étape 1 : un FICHIER, ou un tableau COLLÉ (Ctrl+V) — la suite est la même (2026-10-14).
    const [mode, setMode]               = useState('fichier');
    const [colle, setColle]             = useState('');
    const zoneColleRef = useRef(null);

    // ── Construction des lignes mappées ──────────────────────
    const buildRows = useCallback(() => {
        if (!parsed) return [];
        return parsed.rows
            .filter(r => r.some(cell => cell.trim()))
            .map(r => {
                const obj = { ...contextValues };
                Object.entries(mapping).forEach(([colIdx, champKey]) => {
                    if (champKey) obj[champKey] = r[parseInt(colIdx)] ?? '';
                });
                return obj;
            });
    }, [parsed, mapping, contextValues]);

    // ── Changement de feuille ─────────────────────────────────
    const handleSheetChange = useCallback((name) => {
        if (!wbRef.current || !xlsRef.current) return;
        const data = sheetToData(xlsRef.current, wbRef.current, name);
        setSelectedSheet(name);
        setParsed(data);
        setMapping({});
        const autoMap = {};
        data.headers.forEach((h, i) => {
            const hLow = h.toLowerCase().replace(/[^a-z0-9]/g, '_');
            const match = champs.find(c => {
                const cLow = c.key.toLowerCase();
                const lLow = c.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                return hLow === cLow || hLow === lLow || hLow.includes(cLow) || cLow.includes(hLow);
            });
            if (match) autoMap[i] = match.key;
        });
        setMemoireReprise(appliquerMemoire(memoireCle, data.headers, autoMap, champs));
        setMapping(autoMap);
    }, [champs, memoireCle]);

    // ── Traitement fichier ────────────────────────────────────
    const processFile = useCallback(async (file) => {
        setError(null); setLoading(true);
        try {
            const ext = file.name.split('.').pop().toLowerCase();
            let data;
            if (ext === 'csv' || ext === 'txt') {
                data = parseCSV(await file.text());
                setSheetNames([]); setSelectedSheet('');
                wbRef.current = null; xlsRef.current = null;
            }
            else if (['xlsx', 'xls', 'xlsm'].includes(ext)) {
                data = await parseXLSX(file);
                wbRef.current  = data.workbook;
                xlsRef.current = data.XLSX;
                setSheetNames(data.sheetNames || []);
                setSelectedSheet(data.selectedSheet || '');
            }
            else { setError('Format non supporté (.xlsx, .xlsm, .xls, .csv)'); setLoading(false); return; }
            if (data.headers.length === 0) { setError('Fichier vide ou format non reconnu.'); setLoading(false); return; }
            appliquerDonnees(data, file);
        } catch (err) { setError('Erreur lecture : ' + err.message); }
        finally { setLoading(false); }
    }, [champs, memoireCle]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Un tableau collé : même objet qu'un fichier, même étape 2 ──
    const processColle = () => {
        setError(null);
        const data = parseCollage(colle);
        if (!data.headers.some((h) => h)) { setError('Rien à importer : la zone de collage est vide.'); return; }
        if (!data.rows.length) {
            setError('Une seule ligne collée : copiez aussi la LIGNE DES EN-TÊTES et au moins une ligne de données.');
            return;
        }
        setSheetNames([]); setSelectedSheet('');
        wbRef.current = null; xlsRef.current = null;
        appliquerDonnees(data, { name: NOM_COLLAGE });
    };

    // Un Ctrl+V fait sur l'écran du fichier bascule tout seul vers le collage (un tableau contient des tabulations).
    const collerDirect = (e) => {
        if (mode !== 'fichier' || loading) return;
        const texte = e.clipboardData?.getData('text') || '';
        if (!texte.includes('\t')) return;
        e.preventDefault();
        setMode('coller'); setColle(texte); setError(null);
        setTimeout(() => zoneColleRef.current?.focus(), 0);
    };

    // ── Les en-têtes lus → correspondance proposée, mémoire reprise, étape 2 ──
    const appliquerDonnees = (data, source) => {
            setParsed(data); setFichier(source);
            const autoMap = {};
            data.headers.forEach((h, i) => {
                const hLow = h.toLowerCase().replace(/[^a-z0-9]/g, '_');
                const match = champs.find(c => {
                    const cLow = c.key.toLowerCase();
                    const lLow = c.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                    return hLow.includes(cLow) || cLow.includes(hLow) || hLow.includes(lLow) || lLow.includes(hLow);
                });
                autoMap[i] = match ? match.key : '';
            });
            setMemoireReprise(appliquerMemoire(memoireCle, data.headers, autoMap, champs));
            setMapping(autoMap); setEtape(2);
    };

    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); };

    // ── Vérification contextFields + champs obligatoires ─────
    const validate = () => {
        const ctxMq = contextFields.filter(f => f.required && !contextValues[f.key]);
        if (ctxMq.length > 0) { setError(`Champs requis : ${ctxMq.map(f => f.label).join(', ')}`); return false; }
        const mq = champs.filter(c => c.required && !Object.values(mapping).includes(c.key));
        if (mq.length > 0) { setError(`Colonnes obligatoires non mappées : ${mq.map(c => c.label).join(', ')}`); return false; }
        if (validerCorrespondance) {
            const refus = validerCorrespondance(Object.values(mapping).filter(Boolean));
            if (refus) { setError(refus); return false; }
        }
        // La correspondance est retenue au moment où elle est ACCEPTÉE, pas à chaque clic.
        if (parsed) ecrireMemoire(memoireCle, parsed.headers, mapping);
        return true;
    };
    const meta = { fichier: fichier?.name || null };

    // ── Étape 2 → 3 (diff) ───────────────────────────────────
    const handleGoToDiff = async () => {
        if (!validate()) return;
        setLoading(true); setError(null);
        try {
            const rows = buildRows();
            const items = await onPreview(rows, meta);
            setDiffItems(items);
            // Pré-sélectionner les nouveaux et modifiés — sauf ceux que l'appelant
            // veut décochés (ex. une correction manuelle qu'on écraserait).
            const sel = new Set();
            items.forEach((it, i) => { if (estImportable(it) && !it.decoche) sel.add(i); });
            setDiffSel(sel);
            setEtape(3);
        } catch (err) { setError('Erreur prévisualisation : ' + err.message); }
        finally { setLoading(false); }
    };

    const toggleDiffSel = (idx) => setDiffSel(s => {
        const n = new Set(s); n.has(idx) ? n.delete(idx) : n.add(idx); return n;
    });
    const toggleDiffAll = () => {
        const importables = diffItems.map((it, i) => i).filter(i => estImportable(diffItems[i]));
        const allOn = importables.every(i => diffSel.has(i));
        setDiffSel(s => { const n = new Set(s); importables.forEach(i => allOn ? n.delete(i) : n.add(i)); return n; });
    };

    // ── Import final ──────────────────────────────────────────
    const handleImport = async (rowsOverride) => {
        // Si les lignes sont fournies explicitement (depuis le diff), pas besoin de revalider
        if (!rowsOverride && !validate()) return;
        setLoading(true); setError(null);
        try {
            const rows = rowsOverride || buildRows();
            const res = await onImport(rows, meta);
            setResultat(res); setEtape('R');
        } catch (err) { setError(err.response?.data?.error || err.message || 'Erreur lors de l\'import'); }
        finally { setLoading(false); }
    };

    const handleImportSelected = () => {
        const rows = [...diffSel].map(i => diffItems[i].row);
        handleImport(rows);
    };

    // ── Aperçu (3 premières lignes) ───────────────────────────
    const apercu = parsed?.rows.slice(0, 3).map(r => {
        const obj = {};
        Object.entries(mapping).forEach(([colIdx, champKey]) => { if (champKey) obj[champKey] = r[parseInt(colIdx)] ?? ''; });
        return obj;
    }) || [];

    // ── Labels étapes ─────────────────────────────────────────
    const steps = hasDiff
        ? ['① Fichier ou collage', '② Associer', '③ Vérifier', '④ Résultat']
        : ['① Fichier ou collage', '② Associer les colonnes', '③ Résultat'];
    const etapeNum = etape === 'R' ? (hasDiff ? 4 : 3) : etape;

    const nbNew      = diffItems.filter(d => d.status === 'new').length;
    const nbModified = diffItems.filter(d => d.status === 'modified').length;
    const nbIdentical= diffItems.filter(d => d.status === 'identical').length;
    const nbRejete   = diffItems.filter(d => d.status === 'rejete').length;

    return (
        <div className="imp-overlay" onClick={onClose}>
            <div className="imp-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="imp-header">
                    <div className="imp-header-left">
                        <i className="fa-solid fa-file-import" />
                        <div>
                            <div className="imp-titre">Importer — {titre}</div>
                            <div className="imp-steps">
                                {steps.map((s, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="imp-step-sep">›</span>}
                                        <span className={etapeNum >= i + 1 ? 'imp-step active' : 'imp-step'}>{s}</span>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                    <button className="imp-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>

                <div className="imp-body">
                    {error && <div className="imp-alert">{error}</div>}

                    {/* ── Étape 1 : Fichier ── */}
                    {etape === 1 && (
                        <div className="imp-etape1" onPaste={collerDirect}>
                            {contextFields.length > 0 && (
                                <div className="imp-context-fields">
                                    {contextFields.map(f => (
                                        <div key={f.key} className="imp-context-row">
                                            <label className="imp-context-label">
                                                {f.label}{f.required && <span className="imp-req"> *</span>}
                                            </label>
                                            <select
                                                className={`imp-context-select ${contextValues[f.key] ? 'mapped' : ''}`}
                                                value={contextValues[f.key]}
                                                onChange={e => setContextValues(v => ({ ...v, [f.key]: e.target.value }))}
                                            >
                                                <option value="">— Choisir —</option>
                                                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="imp-explication">
                                <div className="imp-expl-title"><i className="fa-solid fa-circle-info" /> Comment ça marche ?</div>
                                <ol className="imp-expl-list">
                                    <li>Choisissez ou glissez un fichier <strong>.xlsx</strong>, <strong>.xlsm</strong> ou <strong>.csv</strong> — ou <strong>collez</strong> le tableau (Ctrl+V)</li>
                                    <li>Associez chaque colonne à un champ</li>
                                    {hasDiff && <li>Vérifiez les différences avant d'importer</li>}
                                </ol>
                                <div className="imp-expl-tip"><i className="fa-solid fa-lightbulb" /> La 1ère ligne doit contenir les en-têtes.</div>
                            </div>
                            <div className="imp-modes">
                                <button type="button" className={'imp-mode' + (mode === 'fichier' ? ' active' : '')}
                                        onClick={() => { setMode('fichier'); setError(null); }}>
                                    <i className="fa-solid fa-file-excel" /> Fichier
                                </button>
                                <button type="button" className={'imp-mode' + (mode === 'coller' ? ' active' : '')}
                                        onClick={() => { setMode('coller'); setError(null); setTimeout(() => zoneColleRef.current?.focus(), 0); }}>
                                    <i className="fa-solid fa-clipboard" /> Coller (Ctrl+V)
                                </button>
                            </div>
                            {mode === 'coller' && (
                                <div className="imp-coller">
                                    <div className="imp-coller-guide">
                                        <span className="imp-coller-etape"><span className="imp-coller-num">1</span> Ouvrir le tableau (Excel, ENT…)</span>
                                        <i className="fa-solid fa-chevron-right imp-coller-fleche" />
                                        <span className="imp-coller-etape"><span className="imp-coller-num">2</span> Copier le tableau,<strong>&nbsp;en-têtes comprises</strong></span>
                                        <i className="fa-solid fa-chevron-right imp-coller-fleche" />
                                        <span className="imp-coller-etape"><span className="imp-coller-num">3</span> <kbd>Ctrl+V</kbd> ici</span>
                                    </div>
                                    <textarea ref={zoneColleRef} className="imp-coller-zone" value={colle} spellCheck={false}
                                              onChange={(e) => setColle(e.target.value)}
                                              placeholder={'Collez ici (Ctrl+V)…\n\nLa première ligne collée doit être celle des intitulés de colonnes.'} />
                                    <div className="imp-coller-pied">
                                        <span className="imp-coller-compte">
                                            {colle.trim()
                                                ? `${lignesCollees(colle)} ligne${lignesCollees(colle) > 1 ? 's' : ''} collée${lignesCollees(colle) > 1 ? 's' : ''}, en-tête comprise`
                                                : 'Aucun contenu collé'}
                                        </span>
                                        <button type="button" className="imp-btn imp-btn-primary" disabled={!colle.trim()} onClick={processColle}>
                                            Associer les colonnes <i className="fa-solid fa-arrow-right" />
                                        </button>
                                    </div>
                                </div>
                            )}
                            {mode === 'fichier' && (
                            <div className={`imp-dropzone ${dragOver ? 'dragover' : ''} ${loading ? 'loading' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onClick={() => !loading && inputRef.current?.click()}
                            >
                                <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.xls,.csv,.txt"
                                    style={{ display: 'none' }}
                                    onChange={e => e.target.files[0] && processFile(e.target.files[0])} />
                                {loading ? <><i className="fa-solid fa-spinner fa-spin imp-dz-icon" /><span>Lecture...</span></>
                                : dragOver ? <><i className="fa-solid fa-file-arrow-down imp-dz-icon imp-dz-drag" /><span>Relâchez</span></>
                                : <><i className="fa-solid fa-cloud-arrow-up imp-dz-icon" />
                                    <span className="imp-dz-main">Glissez un fichier ici</span>
                                    <span className="imp-dz-sub">ou cliquez pour parcourir</span>
                                    <span className="imp-dz-formats">.xlsx · .xlsm · .xls · .csv</span></>}
                            </div>
                            )}
                        </div>
                    )}

                    {/* ── Étape 2 : Mapping ── */}
                    {etape === 2 && parsed && (
                        <div className="imp-etape2">
                            <div className="imp-fichier-info">
                                <i className={'fa-solid ' + (fichier?.name === NOM_COLLAGE ? 'fa-clipboard' : 'fa-file-excel')} />
                                <strong>{fichier?.name === NOM_COLLAGE ? 'Tableau collé' : fichier?.name}</strong>
                                <span className="imp-badge-count">{parsed.rows.length} ligne{parsed.rows.length > 1 ? 's' : ''}</span>
                                <button className="imp-btn-changer" onClick={() => { setEtape(1); setParsed(null); setSheetNames([]); setSelectedSheet(''); }}>
                                    <i className="fa-solid fa-arrows-rotate" /> Changer
                                </button>
                            </div>
                            {sheetNames.length > 1 && (
                                <div className="imp-sheet-selector">
                                    <i className="fa-solid fa-table-list" />
                                    <label>Feuille :</label>
                                    <select value={selectedSheet} onChange={e => handleSheetChange(e.target.value)}>
                                        {sheetNames.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            )}
                            {memoireCle && memoireReprise > 0 && (
                                <div className="imp-sheet-selector">
                                    <i className="fa-solid fa-clock-rotate-left" />
                                    <span>Correspondance de votre dernier import reprise ({memoireReprise} colonne{memoireReprise > 1 ? 's' : ''}) — modifiable ci-dessous.</span>
                                </div>
                            )}
                            <div className="imp-mapping-titre">Associez les colonnes :</div>
                            <div className="imp-mapping-table">
                                <div className="imp-mapping-header"><span>Colonne fichier</span><span></span><span>Champ application</span></div>
                                {parsed.headers.map((h, i) => {
                                    const refus = nonImportables.find((n) => n.motif && n.motif.test(h || ''));
                                    return (
                                    <React.Fragment key={i}>
                                    <div className="imp-mapping-row">
                                        <div className="imp-col-fichier">
                                            <span className="imp-col-name">{h || `Col ${i+1}`}</span>
                                            <span className="imp-col-sample">{parsed.rows[0]?.[i] || '—'}</span>
                                        </div>
                                        <i className="fa-solid fa-arrow-right imp-arrow" />
                                        <select className={`imp-champ-select ${mapping[i] ? 'mapped' : ''}`}
                                            value={mapping[i] || ''}
                                            onChange={e => setMapping(m => ({ ...m, [i]: e.target.value }))}>
                                            <option value="">— Ignorer —</option>
                                            {champs.map(c => <option key={c.key} value={c.key}>{c.label}{c.required ? ' *' : ''}</option>)}
                                            {nonImportables.length > 0 && (
                                                <optgroup label="Ne s'importe pas ici">
                                                    {nonImportables.map((n, k) => (
                                                        <option key={'ni' + k} value={'__ni' + k} disabled>{n.libelle} — {n.raisonCourte}</option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                    </div>
                                    {refus && (
                                        <div className="imp-mapping-note">
                                            <i className="fa-solid fa-circle-info" /> <span><strong>{refus.libelle}</strong> {refus.message}</span>
                                        </div>
                                    )}
                                    </React.Fragment>
                                    );
                                })}
                            </div>
                            {apercu.length > 0 && Object.values(mapping).some(v => v) && (
                                <div className="imp-apercu">
                                    <div className="imp-apercu-titre"><i className="fa-solid fa-eye" /> Aperçu (3 premières lignes)</div>
                                    <div className="imp-apercu-table-wrap">
                                        <table className="imp-apercu-table">
                                            <thead><tr>{champs.filter(c => Object.values(mapping).includes(c.key)).map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
                                            <tbody>{apercu.map((row, i) => (
                                                <tr key={i}>{champs.filter(c => Object.values(mapping).includes(c.key)).map(c => (
                                                    <td key={c.key}>{row[c.key] || <span className="imp-empty">—</span>}</td>
                                                ))}</tr>
                                            ))}</tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div className="imp-actions">
                                <button className="imp-btn imp-btn-secondary" onClick={() => setEtape(1)}>← Retour</button>
                                {hasDiff ? (
                                    <button className="imp-btn imp-btn-primary" disabled={loading} onClick={handleGoToDiff}>
                                        {loading ? <><i className="fa-solid fa-spinner fa-spin" /> Analyse...</>
                                            : <><i className="fa-solid fa-magnifying-glass" /> Vérifier avant import</>}
                                    </button>
                                ) : (
                                    <button className="imp-btn imp-btn-primary" disabled={loading} onClick={() => handleImport()}>
                                        {loading ? <><i className="fa-solid fa-spinner fa-spin" /> Import...</>
                                            : <><i className="fa-solid fa-file-import" /> Importer {parsed.rows.filter(r => r.some(c => c.trim())).length} lignes</>}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Étape 3 : Diff (uniquement si onPreview) ── */}
                    {etape === 3 && hasDiff && (
                        <div className="imp-etape-diff">
                            {/* En-tête contexte (sous-groupe / semestre sélectionné) */}
                            {contextFields.filter(f => contextValues[f.key]).map(f => {
                                const opt = f.options?.find(o => String(o.value) === String(contextValues[f.key]));
                                return opt ? (
                                    <div key={f.key} className="imp-diff-ctx-header">
                                        <i className="fa-solid fa-graduation-cap" />
                                        <span className="imp-diff-ctx-label">{f.label} :</span>
                                        <span className="imp-diff-ctx-value">{opt.label}</span>
                                    </div>
                                ) : null;
                            })}

                            {/* Résumé */}
                            <div className="imp-diff-summary">
                                <span className="imp-diff-badge new"><i className="fa-solid fa-plus-circle" /> {nbNew} nouveau{nbNew > 1 ? 'x' : ''}</span>
                                <span className="imp-diff-badge modified"><i className="fa-solid fa-pen-to-square" /> {nbModified} modifié{nbModified > 1 ? 's' : ''}</span>
                                <span className="imp-diff-badge identical"><i className="fa-solid fa-equals" /> {nbIdentical} identique{nbIdentical > 1 ? 's' : ''} (ignorés)</span>
                                {nbRejete > 0 && <span className="imp-diff-badge rejete"><i className="fa-solid fa-ban" /> {nbRejete} rejeté{nbRejete > 1 ? 's' : ''}</span>}
                            </div>

                            {/* Sélectionner tout */}
                            {(nbNew + nbModified) > 0 && (
                                <div className="imp-diff-select-all">
                                    <label>
                                        <input type="checkbox"
                                            checked={[...Array(diffItems.length).keys()].filter(i => estImportable(diffItems[i])).every(i => diffSel.has(i))}
                                            onChange={toggleDiffAll} />
                                        <span>Tout sélectionner / désélectionner</span>
                                    </label>
                                </div>
                            )}

                            {/* Liste */}
                            <div className="imp-diff-list">
                                {diffItems.map((item, idx) => (
                                    <div key={idx} className={`imp-diff-row ${item.status}${diffSel.has(idx) ? ' selected' : ''}`}>
                                        <input type="checkbox"
                                            className="imp-diff-check"
                                            checked={diffSel.has(idx)}
                                            disabled={!estImportable(item)}
                                            onChange={() => toggleDiffSel(idx)} />
                                        <div className="imp-diff-content">
                                            <div className="imp-diff-header">
                                                <span className={`imp-diff-status-badge ${item.status}`}>
                                                    {item.status === 'new' ? '🟢 NOUVEAU'
                                                        : item.status === 'modified' ? '🟠 MODIFIÉ'
                                                        : item.status === 'rejete' ? 'REJETÉ'
                                                        : '⬜ IDENTIQUE'}
                                                </span>
                                                {item.libelle ? (
                                                    <span className="imp-diff-nom"><strong>{item.libelle}</strong></span>
                                                ) : (<>
                                                    <code className="prog-code-inline">{item.row.code}</code>
                                                    <span className="imp-diff-nom">{item.row.nom}</span>
                                                </>)}
                                            </div>
                                            {item.note && <div className="imp-diff-note"><i className="fa-solid fa-link" /> {item.note}</div>}
                                            {item.raison && <div className="imp-diff-raison"><i className="fa-solid fa-circle-info" /> {item.raison}</div>}
                                            {item.avertissement && <div className="imp-diff-avert"><i className="fa-solid fa-triangle-exclamation" /> {item.avertissement} — décochée par défaut</div>}
                                            {item.changes && item.changes.length > 0 && (
                                                <div className="imp-diff-changes">
                                                    {item.changes.map((c, ci) => (
                                                        <div key={ci} className="imp-diff-change">
                                                            <span className="imp-diff-field">{c.label}</span>
                                                            <span className="imp-diff-old">{c.old ?? '—'}</span>
                                                            <i className="fa-solid fa-arrow-right imp-diff-arrow" />
                                                            <span className="imp-diff-new">{c.new}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="imp-actions">
                                <button className="imp-btn imp-btn-secondary" onClick={() => setEtape(2)}>← Retour</button>
                                <button className="imp-btn imp-btn-primary"
                                    disabled={diffSel.size === 0 || loading}
                                    onClick={handleImportSelected}>
                                    {loading ? <><i className="fa-solid fa-spinner fa-spin" /> Import...</>
                                        : <><i className="fa-solid fa-file-import" /> Importer {diffSel.size} ligne{diffSel.size > 1 ? 's' : ''}</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Résultat ── */}
                    {etape === 'R' && resultat && (
                        <div className="imp-etape3">
                            <div className={`imp-resultat ${resultat.errors?.length > 0 ? 'partiel' : 'ok'}`}>
                                <i className={`fa-solid ${resultat.errors?.length > 0 ? 'fa-triangle-exclamation' : 'fa-circle-check'} imp-res-icon`} />
                                <div className="imp-res-chiffres">
                                    <div className="imp-res-ok"><span>{resultat.success}</span> importé(s) avec succès</div>
                                    {(resultat.skipped || 0) > 0 && <div className="imp-res-skip"><span>{resultat.skipped}</span> ignoré(s)</div>}
                                </div>
                            </div>
                            {resultat.errors?.length > 0 && (
                                <div className="imp-errors">
                                    <div className="imp-errors-titre">Lignes ignorées :</div>
                                    <ul>{resultat.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                </div>
                            )}
                            <div className="imp-actions">
                                <button className="imp-btn imp-btn-secondary" onClick={() => { setEtape(1); setParsed(null); setResultat(null); setDiffItems([]); }}>
                                    <i className="fa-solid fa-rotate-left" /> Nouvel import
                                </button>
                                <button className="imp-btn imp-btn-primary" onClick={onClose}>
                                    <i className="fa-solid fa-check" /> Fermer
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ImportModal;
